// The five fixed service divisions (12 o'clock, clockwise) + brand colors.
export const DIVISIONS = ['Janitorial', 'Landscaping', 'Security', 'Sweeping', 'General Maintenance'];
export const DIVISION_COLORS = {
  Janitorial: '#063D7D',
  Landscaping: '#4F9417',
  Security: '#DCCC45',
  Sweeping: '#DC8C2F',
  'General Maintenance': '#727272',
};

const WEBSITE_SERVICE_MAP = {
  janitorial: 'Janitorial', landscape: 'Landscaping', landscaping: 'Landscaping',
  maintenance: 'General Maintenance', 'general maintenance': 'General Maintenance',
  sweeping: 'Sweeping', security: 'Security',
};
const TRADE_TO_DIVISION = {
  janitorial: 'Janitorial', landscape: 'Landscaping', landscaping: 'Landscaping',
  security: 'Security', sweeping: 'Sweeping',
  'general maintenance': 'General Maintenance', maintenance: 'General Maintenance',
  hvac: 'General Maintenance', plumbing: 'General Maintenance', electrical: 'General Maintenance',
  handyman: 'General Maintenance',
};

// Mirror of the backend deterministic mapping. Returns {divisions, reviewNeeded}.
export function deriveDivisions(provider) {
  const explicit = (provider?.divisions || []).filter(Boolean);
  if (explicit.length) {
    return { divisions: explicit.filter((d) => DIVISIONS.includes(d)), reviewNeeded: !!provider?.division_review_needed };
  }
  const trades = provider?.trade_types || [];
  const out = [];
  let review = false;
  trades.forEach((raw) => {
    const key = (raw || '').trim().toLowerCase();
    if (!key) return;
    const mapped = WEBSITE_SERVICE_MAP[key] || TRADE_TO_DIVISION[key];
    if (mapped) { if (!out.includes(mapped)) out.push(mapped); }
    else review = true;
  });
  // No services recorded at all -> incomplete (flag for completion, never invent).
  const incomplete = trades.length === 0 && explicit.length === 0;
  return { divisions: out, reviewNeeded: review || incomplete, incomplete };
}

// Does this provider cover the given county (full or partial) for the optional division filter?
export function coversCounty(provider, county, division) {
  const counties = provider?.service_counties || [];
  const partial = provider?.partial_counties || [];
  const covered = counties.includes(county) || partial.includes(county);
  if (!covered) return false;
  if (!division) return true;
  const { divisions } = deriveDivisions(provider);
  return divisions.includes(division);
}

export function isPartialCounty(provider, county) {
  return (provider?.partial_counties || []).includes(county)
    && !(provider?.service_counties || []).includes(county);
}

// Administrative & Business Services classification helpers.
export const ADMIN_CLASSIFICATION = 'Administrative & Business Services';
export const ADMIN_SERVICES = ['Accounting & Bookkeeping'];
export function isAdminProvider(p) {
  return (p?.classifications || []).includes(ADMIN_CLASSIFICATION) || (p?.admin_services || []).length > 0;
}
export function isFieldProvider(p) {
  const { divisions } = deriveDivisions(p);
  return divisions.length > 0 || (p?.classifications || []).includes('Field Services')
    || (p?.trade_types || []).length > 0;
}

// Per-policy expiration status — mirrors backend policy_status (no verification implied).
export const EXPIRING_SOON_DAYS = 30;
export function policyStatus(policy, today = new Date()) {
  if (policy?.superseded) return 'superseded';
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const parse = (d) => { if (!d) return null; const x = new Date(String(d).slice(0, 10)); return isNaN(x) ? undefined : x; };
  const e = parse(policy?.expiration_date);
  const f = parse(policy?.effective_date);
  if (!policy?.expiration_date) return 'date_missing';
  if (e === undefined || (policy?.effective_date && f === undefined) || (e && f && e < f)) return 'invalid_dates';
  if (f && f > t) return 'not_yet_effective';
  if (e < t) return 'expired';
  if ((e - t) / 86400000 <= EXPIRING_SOON_DAYS) return 'expiring_soon';
  return 'current';
}
export const POLICY_STATUS_META = {
  current: { label: 'Current', cls: 'bg-emerald-100 text-emerald-700' },
  expiring_soon: { label: 'Expiring Soon', cls: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Expired', cls: 'bg-rose-100 text-rose-700' },
  not_yet_effective: { label: 'Not Yet Effective', cls: 'bg-sky-100 text-sky-700' },
  date_missing: { label: 'Date Missing', cls: 'bg-slate-100 text-slate-600' },
  invalid_dates: { label: 'Check Dates', cls: 'bg-orange-100 text-orange-800' },
  superseded: { label: 'Superseded', cls: 'bg-slate-100 text-slate-400 line-through' },
};
export function insuranceFlag(p) {
  const pols = (p?.insurance_policies || []).filter((x) => !x.superseded);
  const st = pols.map((x) => policyStatus(x));
  if (st.includes('expired')) return { label: 'Insurance expired', cls: 'bg-rose-100 text-rose-700' };
  if (st.includes('expiring_soon')) return { label: 'Insurance expiring soon', cls: 'bg-amber-100 text-amber-800' };
  if (st.includes('date_missing')) return { label: 'Insurance date missing', cls: 'bg-slate-100 text-slate-600' };
  return null;
}
